/* Linked packs enhanced in place. Delegation and Dawn pub/sub survive section replacement. */
(() => {
  if (window.npmPackSelectorInitialized) return;
  window.npmPackSelectorInitialized = true;
  const selector = '[data-npm-pack-selector]';
  const read = (value) => {
    try { return JSON.parse(value || '[]'); } catch { return []; }
  };
  const update = (widget, source) => {
    const exact = widget.dataset.exactMatch === 'true';
    const matches = (candidate) => source && (candidate.title === source.title ||
      candidate.title === source.options[0] || candidate.options.includes(source.options[0]));
    let unavailable = false;
    widget.querySelectorAll('[data-pack-choice]').forEach((original) => {
      const selected = original.getAttribute('aria-current') === 'true';
      const targets = read(original.dataset.targetVariants);
      const priced = selected ? source : exact ? targets.find(matches) : targets[0];
      const target = selected ? source : exact
        ? targets.find((candidate) => matches(candidate) && candidate.available)
        : targets.find((candidate) => candidate.available);
      const enabled = !!target && /^\d+$/.test(String(target.id)) && !!original.dataset.targetUrl;
      let choice = original;
      if (!selected && choice.tagName !== (enabled ? 'A' : 'SPAN')) {
        const replacement = document.createElement(enabled ? 'a' : 'span');
        replacement.className = choice.className;
        replacement.innerHTML = choice.innerHTML;
        Object.assign(replacement.dataset, choice.dataset);
        replacement.setAttribute('aria-describedby', choice.getAttribute('aria-describedby'));
        choice.replaceWith(replacement);
        choice = replacement;
      }
      if (!selected && enabled) {
        const url = new URL(choice.dataset.targetUrl, document.baseURI);
        url.searchParams.set('variant', String(target.id));
        choice.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        choice.removeAttribute('aria-disabled');
        choice.dataset.npmWidget = 'pack_selector';
        choice.dataset.npmWidgetId = widget.dataset.widgetId;
        choice.dataset.npmTarget = choice.dataset.targetHandle;
      } else if (!selected) {
        choice.removeAttribute('href');
        choice.setAttribute('aria-disabled', 'true');
        delete choice.dataset.npmWidget;
        unavailable = true;
      }
      const display = target || priced;
      choice.querySelector('[data-pack-price]').textContent = display?.money || '';
      const saving = choice.querySelector('[data-pack-saving]');
      const percent = display?.compare > display?.price
        ? Math.round((display.compare - display.price) * 100 / display.compare) : 0;
      saving.textContent = choice.dataset.packSize !== '1' && percent > 0 ? `SAVE ${percent}%` : '';
      saving.hidden = !saving.textContent;
    });
    const explanation = widget.querySelector('[data-pack-unavailable]');
    explanation.textContent = widget.dataset.unavailableText;
    explanation.hidden = !unavailable;
  };
  const fromPicker = (widget) => {
    const sources = read(widget.dataset.sourceVariants);
    const picker = widget.closest('product-info')?.querySelector('variant-selects');
    const values = picker ? Array.from(picker.querySelectorAll('select, input[type="radio"]:checked'), (input) => input.value) : [];
    if (values.length) {
      return sources.find((source) => source.options.length === values.length &&
        source.options.every((value, index) => value === values[index]));
    }
    return sources.find((source) => String(source.id) === widget.dataset.sourceVariant);
  };
  const scan = (root) => {
    if (root.matches?.(selector)) update(root, fromPicker(root));
    root.querySelectorAll?.(selector).forEach((widget) => update(widget, fromPicker(widget)));
  };
  let pending;
  let packHistory = false;
  const restoreControls = (request) => {
    if (!request) return;
    const restore = (node, name, value) => value === null
      ? node.removeAttribute(name) : node.setAttribute(name, value);
    restore(request.widget, 'aria-busy', request.busy);
    request.controls.forEach(({ choice, disabled }) => restore(choice, 'aria-disabled', disabled));
  };
  // Full navigation is reserved for a history entry that cannot safely be restored.
  const restoreHistoryFallback = (url) => window.location.assign(url);
  const loadPack = async (widget, href, push, packSize) => {
    pending?.controller.abort();
    restoreControls(pending);
    const request = {
      controller: new AbortController(), widget,
      busy: widget.getAttribute('aria-busy'),
      controls: Array.from(widget.querySelectorAll('[data-pack-choice]'), (choice) => ({
        choice, disabled: choice.getAttribute('aria-disabled'),
      })),
    };
    pending = request;
    widget.setAttribute('aria-busy', 'true');
    request.controls.forEach(({ choice }) => choice.setAttribute('aria-disabled', 'true'));
    try {
      const url = new URL(href, document.baseURI);
      if (url.origin !== window.location.origin) throw new Error('Cross-origin pack URL');
      const owner = widget.closest('product-info');
      const sectionId = widget.dataset.sectionId;
      const section = document.getElementById(`shopify-section-${sectionId}`);
      const current = section || owner;
      if (!owner || !current?.isConnected) throw new Error('Missing current product surface');
      const response = await fetch(href, { signal: request.controller.signal });
      if (!response.ok) throw new Error('Pack response failed');
      const markup = await response.text();
      if (pending !== request || request.controller.signal.aborted) return;
      const html = new DOMParser().parseFromString(markup, 'text/html');
      if (!html || html.querySelector('parsererror')) throw new Error('Invalid pack document');
      const targetSection = section && html.getElementById(section.id);
      const target = targetSection || html.getElementById(owner.id) ||
        html.querySelector("product-info[id^='MainProduct-']");
      const targetOwner = targetSection ? targetSection.querySelector('product-info') : target;
      if (!target || !targetOwner || !targetOwner.querySelector(selector)) {
        throw new Error('Missing target product surface');
      }
      const currentSpecSheet = document.querySelector('.tj-spec-sheet-section');
      const targetSpecSheet = html.querySelector('.tj-spec-sheet-section');
      if (currentSpecSheet && !targetSpecSheet) throw new Error('Missing target Spec Sheet');
      // The standalone presentation section never needs fetched scripts.
      targetSpecSheet?.querySelectorAll('script').forEach((script) => script.remove());
      // Keep inert variant/structured data; never re-inject executable fetched scripts.
      target.querySelectorAll('script').forEach((script) => {
        if (!['application/json', 'application/ld+json'].includes(script.getAttribute('type'))) script.remove();
      });
      target.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
      if (pending !== request || request.controller.signal.aborted || !current.isConnected) return;
      // An old Dawn variant fetch must not finish against the replacement product.
      owner.abortController?.abort();
      // Dawn moves these out to body on connection; remove the previous product's copies.
      document.querySelectorAll('product-modal, modal-dialog').forEach((modal) => {
        if (modal.dataset.section === owner.dataset.section) modal.remove();
      });
      (targetSection ? current : owner).replaceWith(target);
      if (currentSpecSheet) currentSpecSheet.replaceWith(targetSpecSheet);
      if (html.title) document.title = html.title;
      if (push) {
        window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
        packHistory = true;
      }
      const newWidget = targetOwner.querySelector(selector);
      const selected = Array.from(newWidget.querySelectorAll('[data-pack-choice]')).find((choice) =>
        choice.getAttribute('aria-current') === 'true' && (!packSize || choice.dataset.packSize === packSize));
      const focusTarget = selected || newWidget.querySelector('legend');
      focusTarget?.setAttribute('tabindex', '-1');
      focusTarget?.focus({ preventScroll: true });
      window.Shopify?.PaymentButton?.init();
      window.ProductModel?.loadShopifyXR();
    } catch {
      if (pending === request && !request.controller.signal.aborted && !push) restoreHistoryFallback(href);
    } finally {
      if (pending === request) {
        restoreControls(request);
        pending = undefined;
      }
    }
  };
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const choice = event.target.closest?.('[data-pack-choice]');
    const widget = choice?.closest(selector);
    if (!widget || choice.tagName !== 'A' || !choice.getAttribute('href') ||
      choice.getAttribute('download') !== null ||
      (choice.getAttribute('target') && choice.getAttribute('target') !== '_self')) return;
    // Busy links remain eligible to supersede a request; originally disabled links do not.
    const disabled = pending?.controls.find((control) => control.choice === choice)?.disabled ??
      (pending?.widget === widget ? null : choice.getAttribute('aria-disabled'));
    if (disabled === 'true') return;
    event.preventDefault();
    loadPack(widget, choice.getAttribute('href'), true, choice.dataset.packSize);
  });
  window.addEventListener('popstate', () => {
    if (!packHistory && !pending) return;
    const widget = document.querySelector(selector);
    if (widget) loadPack(widget, window.location.href, false);
    else {
      pending?.controller.abort();
      restoreControls(pending);
      pending = undefined;
      restoreHistoryFallback(window.location.href);
    }
  });
  document.addEventListener('change', (event) => {
    const owner = event.target.closest?.('variant-selects')?.closest('product-info');
    if (!owner) return;
    owner.querySelectorAll(selector).forEach((widget) => update(widget, fromPicker(widget)));
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      document.querySelectorAll(selector).forEach((widget) => {
        if (widget.dataset.sectionId !== String(data.sectionId)) return;
        const source = read(widget.dataset.sourceVariants).find((candidate) => String(candidate.id) === String(data.variant?.id));
        update(widget, source);
      });
    });
  }
  scan(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) scan(node);
    }));
  }).observe(document.documentElement, {childList: true, subtree: true});
})();
